import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Typography,
    ThemeProvider,
    Box,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    NativeSelect,
    FormControl,
    InputLabel,
    Divider,
    Chip,
    Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { orderBy } from 'lodash';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import FooterPadding from '../main/footerpadding';
import Footer from '../main/footer';
import AddIcon from '@mui/icons-material/Add';
import vadoTheme from '../main/VadoThemeMui5'; // Import VadoTheme for mui 5
import Contract from '../domain/contract';
import { getContractData, updateContractData, deleteContract, getUsage, createContract } from '../modules/contractdata';
import { updateParameter } from '../modules/parameterdata';
import { listAllTenants } from '../modules/tenantdata';
import DeleteConfirmDialog from '../common/deleteconfirmdialog';
import ParameterSelector from '../common/parameterselector';

export default function ContractMgmt(props) {
    const contractsById = useSelector((state) => state.contractdata.contractsById);
    const loadingContracts = useSelector((state) => state.contractdata.loadingContracts);
    const parameters = useSelector((state) => state.parameterdata.parameters);
    const tenants = useSelector((state) => state.tenantdata.allTenants);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [saving, setSaving] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [contractToDelete, setContractToDelete] = useState(null);
    const [editingContractId, setEditingContractId] = useState(null);
    const [editedContract, setEditedContract] = useState(null);
    const [searchFilter, setSearchFilter] = useState('');
    const [tenantFilter, setTenantFilter] = useState(null); // null = show all tenants' contracts
    const [usageDialogOpen, setUsageDialogOpen] = useState(false);
    const [usageDialogType, setUsageDialogType] = useState(''); // 'customers' or 'locations'
    const [usageDialogItems, setUsageDialogItems] = useState([]);
    const [usageItemsByContractId, setUsageItemsByContractId] = useState({});
    const [loadingUsageByContractId, setLoadingUsageByContractId] = useState({});
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newContractNumber, setNewContractNumber] = useState('');
    const [contractNumberError, setContractNumberError] = useState('');
    const [newContractTenantClusterId, setNewContractTenantClusterId] = useState('');
    const [newContractTenantError, setNewContractTenantError] = useState('');
    const [editedContractName, setEditedContractName] = useState('');
    const [editedContractNameError, setEditedContractNameError] = useState('');

    // allow multiple expanded panels: map of panelId -> boolean
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        if (!loadingContracts && (!contractsById || Object.keys(contractsById).length === 0)) {
            dispatch(getContractData());
        }
    }, [dispatch, contractsById, loadingContracts]);

    useEffect(() => {
        if (!tenants || tenants.length === 0) {
            dispatch(listAllTenants());
        }
    }, [dispatch, tenants]);

    const tenantNameByClusterId = (tenants || []).reduce((acc, tenant) => {
        acc[tenant.clusterId] = tenant.name;
        return acc;
    }, {});

    // Admin contract management is cross-tenant: group by tenant (with a spacer between groups) rather
    // than hiding/filtering by a "current" tenant, which doesn't apply to this view at all.
    const contractTenantName = (contract) =>
        tenantNameByClusterId[contract.tenantCompanyClusterId] || t('contract.unknownTenant') || 'Unknown tenant';

    // Convert contracts object to array and order by contract number
    const contracts = contractsById ? Object.values(contractsById) : [];
    const orderedContracts = orderBy(
        contracts,
        [(c) => contractTenantName(c), (c) => c.contractNumber.value],
        ['asc', 'asc']
    );
    const filteredContracts = orderedContracts.filter((contract) => {
        if (tenantFilter !== null && contract.tenantCompanyClusterId !== tenantFilter) return false;
        if (!searchFilter) return true;
        const searchLower = searchFilter.toLowerCase();
        const contractNumber = contract.contractNumber.value ? contract.contractNumber.value.toLowerCase() : '';
        return contractNumber.includes(searchLower);
    });

    const sortedTenants = orderBy(tenants || [], [(tenant) => tenant.name], ['asc']);

    const handleTenantFilterClick = (clusterId) => () => {
        setTenantFilter(tenantFilter === clusterId ? null : clusterId);
    };

    const handleAccordionChange = (contractId) => async (event, isExpanded) => {
        setExpanded({
            ...expanded,
            [contractId]: isExpanded,
        });

        // Fetch usage data when accordion is opened if not already cached
        if (isExpanded && !usageItemsByContractId[contractId]) {
            setLoadingUsageByContractId({
                ...loadingUsageByContractId,
                [contractId]: true,
            });
            const data = await getUsage(contractId);
            setUsageItemsByContractId({
                ...usageItemsByContractId,
                [contractId]: data,
            });
            setLoadingUsageByContractId({
                ...loadingUsageByContractId,
                [contractId]: false,
            });
        }
    };

    const handleDeleteClick = (contract) => {
        setContractToDelete(contract);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setContractToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (contractToDelete) {
            // TODO: Implement delete contract API call
            await deleteContract(contractToDelete.contractNumber.id)(dispatch);
            console.log('Deleting contract:', contractToDelete.contractNumber.value);
        }
        setDeleteConfirmOpen(false);
        setContractToDelete(null);
    };

    const handleEditClick = (contract) => {
        setEditingContractId(contract.contractNumber.id);
        setEditedContract(new Contract(contract));
        setEditedContractName(contract.contractNumber.value || '');
        setEditedContractNameError('');
    };

    const handleCancelEdit = () => {
        setEditingContractId(null);
        setEditedContract(null);
        setEditedContractName('');
        setEditedContractNameError('');
    };

    const handleSaveEdit = async () => {
        if (!editedContractName.trim()) {
            setEditedContractNameError(t('contract.nameTooShortOrMissing'));
            return;
        }
        const isDuplicate = contracts.some(
            (c) =>
                c.contractNumber.id !== editedContract.contractNumber.id &&
                c.contractNumber.value.toLowerCase() === editedContractName.trim().toLowerCase()
        );
        if (isDuplicate) {
            setEditedContractNameError(t('contract.warningAlreadyExists'));
            return;
        }
        if (editedContract) {
            setSaving(true);
            if (editedContractName !== editedContract.contractNumber.value) {
                await updateParameter({
                    id: editedContract.contractNumber.id,
                    storageId: editedContract.contractNumber.storageId,
                    value: editedContractName,
                    category: 'ContractNumber',
                })(dispatch);
            }
            await updateContractData(editedContract)(dispatch);
            setSaving(false);
            setEditingContractId(null);
            setEditedContract(null);
            setEditedContractName('');
            setEditedContractNameError('');
        }
    };

    const handleParameterChange = (name, value) => {
        const nameOfProp = name[0].toLowerCase() + name.slice(1);
        const updatedContract = new Contract(editedContract);
        updatedContract[nameOfProp] = parameters[name].find((item) => item.id === value);
        setEditedContract(updatedContract);
    };

    const handleFieldChange = (fieldName, value) => {
        const updatedContract = new Contract(editedContract);
        updatedContract[fieldName] = value;
        setEditedContract(updatedContract);
    };

    const handleEditedContractNameChange = (e) => {
        const val = e.target.value;
        setEditedContractName(val);
        if (!val.trim()) {
            setEditedContractNameError(t('contract.nameTooShortOrMissing'));
        } else if (
            contracts.some(
                (c) =>
                    c.contractNumber.id !== editedContract.contractNumber.id &&
                    c.contractNumber.value.toLowerCase() === val.trim().toLowerCase()
            )
        ) {
            setEditedContractNameError(t('contract.warningAlreadyExists'));
        } else {
            setEditedContractNameError('');
        }
    };

    const handleOpenUsageDialog = async (type, contractId) => {
        // Check if data is already cached
        if (!usageItemsByContractId[contractId]) {
            // Fetch and cache the data
            const data = await getUsage(contractId);
            setUsageItemsByContractId({
                ...usageItemsByContractId,
                [contractId]: data,
            });

            // Set dialog data based on type
            setUsageDialogType(type);
            setUsageDialogItems(type === 'customers' ? data.customers : data.locations);
            setUsageDialogOpen(true);
        } else {
            // Use cached data
            const cachedData = usageItemsByContractId[contractId];
            setUsageDialogType(type);
            setUsageDialogItems(type === 'customers' ? cachedData.customers : cachedData.locations);
            setUsageDialogOpen(true);
        }
    };

    const handleCloseUsageDialog = () => {
        setUsageDialogOpen(false);
        setUsageDialogItems([]);
        setUsageDialogType('');
    };

    const handleAddClick = () => {
        setAddDialogOpen(true);
        setNewContractNumber('');
        setContractNumberError('');
        setNewContractTenantClusterId('');
        setNewContractTenantError('');
    };

    const handleAddDialogClose = () => {
        setAddDialogOpen(false);
        setNewContractNumber('');
        setContractNumberError('');
        setNewContractTenantClusterId('');
        setNewContractTenantError('');
    };

    const handleNewContractTenantChange = (event) => {
        const value = event.target.value;
        setNewContractTenantClusterId(value);
        setNewContractTenantError(value ? '' : t('contract.tenantRequired'));
    };

    const handleContractNumberChange = (event) => {
        const value = event.target.value;
        setNewContractNumber(value);

        // Check if contract name already exists
        const exists = contracts.some(
            (contract) => contract.contractNumber.value.toLowerCase() === value.toLowerCase()
        );

        if (exists) {
            setContractNumberError(t('contract.warningAlreadyExists'));
        } else {
            setContractNumberError('');
        }
    };

    const handleCreateContract = async () => {
        if (!newContractNumber.trim()) {
            setContractNumberError(t('contract.nameTooShortOrMissing'));
            return;
        }

        if (!newContractTenantClusterId) {
            setNewContractTenantError(t('contract.tenantRequired'));
            return;
        }

        // Check if contract name already exists
        const exists = contracts.some(
            (contract) => contract.contractNumber.value.toLowerCase() === newContractNumber.toLowerCase()
        );

        if (exists) {
            setContractNumberError(t('contract.warningAlreadyExists'));
            return;
        }

        setSaving(true);
        try {
            await createContract({ value: newContractNumber }, newContractTenantClusterId)(dispatch);
            handleAddDialogClose();
        } catch (error) {
            console.error('Error creating contract:', error);
        } finally {
            setSaving(false);
        }
    };

    const leadSourceText = (value) => {
        switch (value) {
            case 0:
                return t('contract.leadSourceNotDefined');
            case 1:
                return t('contract.leadSourceCompany');
            default:
                return '';
        }
    };

    return (
        <ThemeProvider theme={vadoTheme}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label={t('reports.search')}
                        placeholder={''}
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        margin="normal"
                        variant="standard"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                            label={t('general.all') || 'All'}
                            color={tenantFilter === null ? 'primary' : 'default'}
                            variant={tenantFilter === null ? 'filled' : 'outlined'}
                            onClick={handleTenantFilterClick(null)}
                        />
                        {sortedTenants.map((tenant) => (
                            <Chip
                                key={tenant.clusterId}
                                label={tenant.name}
                                color={tenantFilter === tenant.clusterId ? 'primary' : 'default'}
                                variant={tenantFilter === tenant.clusterId ? 'filled' : 'outlined'}
                                onClick={handleTenantFilterClick(tenant.clusterId)}
                            />
                        ))}
                    </Stack>
                </Grid>

                {loadingContracts && (
                    <Grid item xs={12}>
                        <Typography>{t('general.loading') || 'Loading...'}</Typography>
                    </Grid>
                )}

                {!loadingContracts && filteredContracts.length === 0 && (
                    <Grid item xs={12}>
                        <Typography>{t('general.noSearchResults')}</Typography>
                    </Grid>
                )}

                {!loadingContracts &&
                    filteredContracts.map((contract, index) => {
                        const tenantName = contractTenantName(contract);
                        const showTenantHeader = index === 0 || tenantName !== contractTenantName(filteredContracts[index - 1]);
                        return (
                            <React.Fragment key={contract.contractNumber.id || Math.random()}>
                                {showTenantHeader && (
                                    <Grid item xs={12}>
                                        {index > 0 && <Divider sx={{ my: 1 }} />}
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: index > 0 ? 1 : 0 }}>
                                            {tenantName}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Accordion
                                        expanded={expanded[contract.contractNumber.id] || false}
                                        onChange={handleAccordionChange(contract.contractNumber.id)}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls={`contract-${contract.contractNumber.id}-content`}
                                            id={`contract-${contract.contractNumber.id}-header`}>
                                            <Typography variant="h6">{contract.contractNumber.value || 'N/A'}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                {editingContractId === contract.contractNumber.id ? (
                                                    <>
                                                        <Grid item xs={12} md={6}>
                                                    <TextField
                                                        variant="standard"
                                                        label={t('contract.contractName')}
                                                        sx={{ mt: 2, ml: 1, mr: 1, width: '95%' }}
                                                        value={editedContractName}
                                                        onChange={handleEditedContractNameChange}
                                                        error={!!editedContractNameError}
                                                        helperText={editedContractNameError}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    {parameters && (
                                                        <ParameterSelector
                                                            values={parameters}
                                                            category={'InvoicingTerms'}
                                                            selected={
                                                                editedContract ? editedContract.invoicingTerms : null
                                                            }
                                                            handleChange={handleParameterChange}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    {parameters && (
                                                        <ParameterSelector
                                                            values={parameters}
                                                            category={'DeliveryTerms'}
                                                            selected={
                                                                editedContract ? editedContract.deliveryTerms : null
                                                            }
                                                            handleChange={handleParameterChange}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        variant="standard"
                                                        label={t('contract.annualRebate')}
                                                        type="number"
                                                        sx={{ mt: 2, ml: 1, mr: 1, width: '95%' }}
                                                        inputProps={{
                                                            step: '0.01',
                                                            min: '0',
                                                        }}
                                                        value={
                                                            editedContract && editedContract.annualRebate != null
                                                                ? editedContract.annualRebate
                                                                : ''
                                                        }
                                                        onChange={(e) =>
                                                            handleFieldChange(
                                                                'annualRebate',
                                                                e.target.value ? parseFloat(e.target.value) : null
                                                            )
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FormControl sx={{ mt: 2, ml: 1, mr: 1, width: '95%' }}>
                                                        <InputLabel variant="standard" id="leadSource-label">
                                                            {t('contract.leadSource')}
                                                        </InputLabel>
                                                        <NativeSelect
                                                            id="leadSource"
                                                            value={
                                                                editedContract && editedContract.leadOrigin != null
                                                                    ? editedContract.leadOrigin
                                                                    : 0
                                                            }
                                                            onChange={(e) =>
                                                                handleFieldChange(
                                                                    'leadOrigin',
                                                                    parseInt(e.target.value, 10)
                                                                )
                                                            }>
                                                            <option value={0}>{leadSourceText(0)}</option>
                                                            <option value={1}>{leadSourceText(1)}</option>
                                                        </NativeSelect>
                                                    </FormControl>
                                                </Grid>{' '}
                                            </>
                                        ) : (
                                            <>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" color="textSecondary">
                                                        {t('parameterCategories.InvoicingTerms')}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {contract.invoicingTerms
                                                            ? contract.invoicingTerms.value
                                                            : 'N/A'}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" color="textSecondary">
                                                        {t('parameterCategories.DeliveryTerms')}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {contract.deliveryTerms ? contract.deliveryTerms.value : 'N/A'}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" color="textSecondary">
                                                        {t('contract.annualRebate')}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {contract.annualRebate != null ? contract.annualRebate : '0'} %
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" color="textSecondary">
                                                        {t('contract.leadSource')}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {leadSourceText(contract.leadOrigin)}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        )}

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                                {t('contract.usage')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                                                <Typography variant="body2">
                                                    {t('general.customer')}:{' '}
                                                    {loadingUsageByContractId[contract.contractNumber.id] ? (
                                                        <CircularProgress size={16} sx={{ ml: 1 }} />
                                                    ) : usageItemsByContractId[contract.contractNumber.id] ? (
                                                        <Link
                                                            component="button"
                                                            variant="body2"
                                                            onClick={() =>
                                                                handleOpenUsageDialog(
                                                                    'customers',
                                                                    contract.contractNumber.id
                                                                )
                                                            }
                                                            sx={{ fontWeight: 'bold', cursor: 'pointer' }}>
                                                            {
                                                                usageItemsByContractId[contract.contractNumber.id]
                                                                    .customers.length
                                                            }
                                                        </Link>
                                                    ) : (
                                                        <span style={{ fontWeight: 'bold' }}>-</span>
                                                    )}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {t('general.location')}:{' '}
                                                    {loadingUsageByContractId[contract.contractNumber.id] ? (
                                                        <CircularProgress size={16} sx={{ ml: 1 }} />
                                                    ) : usageItemsByContractId[contract.contractNumber.id] ? (
                                                        <Link
                                                            component="button"
                                                            variant="body2"
                                                            onClick={() =>
                                                                handleOpenUsageDialog(
                                                                    'locations',
                                                                    contract.contractNumber.id
                                                                )
                                                            }
                                                            sx={{ fontWeight: 'bold', cursor: 'pointer' }}>
                                                            {
                                                                usageItemsByContractId[contract.contractNumber.id]
                                                                    .locations.length
                                                            }
                                                        </Link>
                                                    ) : (
                                                        <span style={{ fontWeight: 'bold' }}>-</span>
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, mt: 2 }}>
                                                {editingContractId === contract.contractNumber.id ? (
                                                    <>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                                            onClick={handleSaveEdit}
                                                            disabled={saving || !!editedContractNameError || !editedContractName.trim()}>
                                                            {t('buttons.save') || 'Save'}
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<CancelIcon />}
                                                            onClick={handleCancelEdit}
                                                            disabled={saving}>
                                                            {t('buttons.cancel') || 'Cancel'}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={() => handleDeleteClick(contract)}
                                                            disabled={saving}>
                                                            {t('buttons.remove') || 'Delete'}
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        startIcon={<EditIcon />}
                                                        onClick={() => handleEditClick(contract)}
                                                        disabled={editingContractId !== null}>
                                                        {t('buttons.edit') || 'Edit'}
                                                    </Button>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                            </React.Fragment>
                        );
                    })}

                <Grid item xs={12}>
                    <Footer>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                disabled={saving}
                                onClick={handleAddClick}>
                                {t('buttons.add')}
                            </Button>
                        </Grid>
                    </Footer>
                    <FooterPadding />
                </Grid>
            </Grid>

            <DeleteConfirmDialog
                open={deleteConfirmOpen}
                customMessage={
                    <>
                        {t('general.confirmRemove')}{' '}
                        <strong>{contractToDelete ? contractToDelete.contractNumber.value : ''}</strong>?<br />
                        <br />
                        <em>{t('contract.removalNotice')}</em>
                    </>
                }
                itemToDelete={contractToDelete ? contractToDelete.contractNumber.value : ''}
                confirmDelete={handleDeleteConfirm}
                cancel={handleDeleteCancel}
            />

            <Dialog open={usageDialogOpen} onClose={handleCloseUsageDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {usageDialogType === 'customers' ? t('general.customers') : t('general.locations')}
                </DialogTitle>
                <DialogContent>
                    {usageDialogItems.length === 0 ? (
                        <Typography> - </Typography>
                    ) : (
                        <List>
                            {usageDialogItems.map((item, index) => (
                                <ListItem key={index}>
                                    <ListItemText primary={item} />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUsageDialog}>{t('buttons.close') || 'Close'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addDialogOpen} onClose={handleAddDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>{t('contract.add')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('parameterCategories.ContractNumber')}
                        type="text"
                        fullWidth
                        value={newContractNumber}
                        onChange={handleContractNumberChange}
                        error={!!contractNumberError}
                        helperText={contractNumberError}
                        disabled={saving}
                    />
                    <FormControl fullWidth margin="dense" error={!!newContractTenantError}>
                        <InputLabel htmlFor="new-contract-tenant">{t('contract.tenant')}</InputLabel>
                        <NativeSelect
                            id="new-contract-tenant"
                            value={newContractTenantClusterId}
                            onChange={handleNewContractTenantChange}
                            disabled={saving}>
                            <option value="" />
                            {(tenants || []).map((tenant) => (
                                <option key={tenant.clusterId} value={tenant.clusterId}>
                                    {tenant.name}
                                </option>
                            ))}
                        </NativeSelect>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAddDialogClose} disabled={saving}>
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        onClick={handleCreateContract}
                        variant="contained"
                        color="primary"
                        disabled={saving || !!contractNumberError || !newContractNumber.trim() || !newContractTenantClusterId}>
                        {saving ? t('toast.saving') : t('buttons.create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}
